import React, { useEffect, useRef, useState, useCallback } from 'react';
import { convertHeicUrlToObjectUrl, convertHeicBlobToJpeg, isHeicUrl, isHeicBlob } from '@/services/heicConversion';

interface HeicSafeImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
}

/**
 * Drop-in replacement for <img> that handles HEIC/HEIF images in two ways:
 *
 * 1. URL-based detection: if `src` has a .heic/.heif extension, it fetches,
 *    converts to JPEG, and renders the result. (Handles stored HEIC files.)
 *
 * 2. onError fallback: if the browser can't render a blob: URL (e.g. because
 *    upstream HEIC→JPEG conversion failed and the raw HEIC blob was used),
 *    it fetches the blob, checks its type, and attempts conversion.
 *    (Handles the case where heic2any failed during upload.)
 */
const HeicSafeImage: React.FC<HeicSafeImageProps> = ({ src, onError: parentOnError, ...props }) => {
  const [displaySrc, setDisplaySrc] = useState<string>(isHeicUrl(src) ? '' : src);
  const objectUrlRef = useRef<string | null>(null);
  // Prevent re-entrant error handling after a successful fallback conversion
  const fallbackAttemptedRef = useRef(false);

  useEffect(() => {
    // Reset fallback flag whenever the source changes
    fallbackAttemptedRef.current = false;

    if (!isHeicUrl(src)) {
      setDisplaySrc(src);
      return;
    }

    let cancelled = false;

    convertHeicUrlToObjectUrl(src)
      .then((objectUrl) => {
        if (cancelled) { URL.revokeObjectURL(objectUrl); return; }
        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = objectUrl;
        setDisplaySrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setDisplaySrc(src); // fallback to original
      });

    return () => { cancelled = true; };
  }, [src]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  const handleError = useCallback(
    async (e: React.SyntheticEvent<HTMLImageElement>) => {
      // Only attempt blob-level HEIC recovery once per src
      if (!fallbackAttemptedRef.current && src.startsWith('blob:')) {
        fallbackAttemptedRef.current = true;
        try {
          const response = await fetch(src);
          const blob = await response.blob();
          // Only bother converting if the blob type is HEIC-like or ambiguous
          if (isHeicBlob(blob)) {
            const jpegBlob = await convertHeicBlobToJpeg(blob);
            const objectUrl = URL.createObjectURL(jpegBlob);
            if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = objectUrl;
            setDisplaySrc(objectUrl);
            return; // don't call parentOnError — we recovered
          }
        } catch {
          // Conversion failed; fall through to parentOnError
        }
      }
      parentOnError?.(e);
    },
    [src, parentOnError]
  );

  if (!displaySrc) return null;

  return <img src={displaySrc} onError={handleError} {...props} />;
};

export default HeicSafeImage;
